import type { RefObject } from "react"
import { useEffect, useRef, useState } from "react"

type InlineClampResult = Readonly<{
    previewText: string
    isTruncated: boolean
}>

type UseInlineClampedTextParams = Readonly<{
    fullText: string
    maxLines: number
    suffixText: string
    suffixExtraEmWidth?: number
    measureRef: RefObject<HTMLElement | null>
    measurePrefixRef: RefObject<HTMLElement | null>
    measureSuffixRef: RefObject<HTMLElement | null>
    observeSizeRef: RefObject<HTMLElement | null>
}>

function safeParsePx(value: string): number | null {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
}

function getLineHeightPx(element: HTMLElement, prefixEl: HTMLElement, suffixEl: HTMLElement): number {
    const computed = window.getComputedStyle(element)
    const maybePx = computed.lineHeight.endsWith("px") ? safeParsePx(computed.lineHeight) : null
    if (maybePx != null) return maybePx

    const prevPrefix = prefixEl.textContent ?? ""
    const prevSuffixDisplay = suffixEl.style.display

    try {
        suffixEl.style.display = "none"
        prefixEl.textContent = "A"
        const height = element.getBoundingClientRect().height
        return Math.max(1, height)
    } finally {
        prefixEl.textContent = prevPrefix
        suffixEl.style.display = prevSuffixDisplay
    }
}

function fitsWithinLines(element: HTMLElement, maxHeight: number): boolean {
    // Tiny epsilon to avoid flakiness due to subpixel rounding.
    return element.getBoundingClientRect().height <= maxHeight + 0.5
}

export function useInlineClampedText({
    fullText,
    maxLines,
    suffixText,
    suffixExtraEmWidth = 0,
    measureRef,
    measurePrefixRef,
    measureSuffixRef,
    observeSizeRef,
}: UseInlineClampedTextParams): InlineClampResult {
    const [result, setResult] = useState<InlineClampResult>({ previewText: fullText, isTruncated: false })
    const rafIdRef = useRef<number | null>(null)

    useEffect(() => {
        const measureEl = measureRef.current
        const prefixEl = measurePrefixRef.current
        const suffixEl = measureSuffixRef.current
        const observeEl = observeSizeRef.current

        if (!measureEl || !prefixEl || !suffixEl || !observeEl) return

        const recompute = () => {
            const width = observeEl.getBoundingClientRect().width
            if (!Number.isFinite(width) || width <= 0) return

            measureEl.style.width = `${width}px`

            const lineHeightPx = getLineHeightPx(measureEl, prefixEl, suffixEl)
            const maxHeight = lineHeightPx * maxLines

            // 1) Check if full text fits (reserving suffixExtraEmWidth for an always-present inline icon).
            const prevSuffixDisplay = suffixEl.style.display
            const prevSuffixPaddingLeft = suffixEl.style.paddingLeft
            suffixEl.textContent = ""
            if (suffixExtraEmWidth > 0) {
                suffixEl.style.display = prevSuffixDisplay || ""
                suffixEl.style.paddingLeft = `${suffixExtraEmWidth}em`
            } else {
                suffixEl.style.display = "none"
            }
            prefixEl.textContent = fullText

            const fullFits = fitsWithinLines(measureEl, maxHeight)
            if (fullFits) {
                setResult({ previewText: fullText, isTruncated: false })
                suffixEl.style.display = prevSuffixDisplay
                suffixEl.style.paddingLeft = prevSuffixPaddingLeft
                return
            }

            // 2) Needs truncation: show suffix and find maximal prefix that fits with suffix.
            suffixEl.style.display = prevSuffixDisplay || ""
            suffixEl.textContent = suffixText

            // Optional extra width reservation to account for inline icon/spacing when suffix is rendered
            if (suffixExtraEmWidth > 0) {
                suffixEl.style.paddingLeft = `${suffixExtraEmWidth}em`
            } else {
                suffixEl.style.paddingLeft = ""
            }

            let low = 0
            let high = fullText.length
            let best = ""

            while (low <= high) {
                const mid = Math.floor((low + high) / 2)
                const candidate = fullText.slice(0, mid).trimEnd()
                prefixEl.textContent = candidate

                if (fitsWithinLines(measureEl, maxHeight)) {
                    best = candidate
                    low = mid + 1
                } else {
                    high = mid - 1
                }
            }

            setResult({ previewText: best, isTruncated: true })
        }

        const scheduleRecompute = () => {
            if (rafIdRef.current != null) window.cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = window.requestAnimationFrame(() => {
                rafIdRef.current = null
                recompute()
            })
        }

        scheduleRecompute()

        const ro = new ResizeObserver(() => scheduleRecompute())
        ro.observe(observeEl)

        return () => {
            ro.disconnect()
            if (rafIdRef.current != null) window.cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = null
        }
    }, [
        fullText,
        maxLines,
        measurePrefixRef,
        measureRef,
        measureSuffixRef,
        observeSizeRef,
        suffixExtraEmWidth,
        suffixText,
    ])

    return result
}
